import { Suspense, lazy } from 'react'
// redux
import { useSelector } from 'react-redux'
// route
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'
import Loading from 'components/atoms/loading/loading'
// default routes control
import PrivateRoute from 'components/atoms/private-route/private-route'
import PublicRoute from 'components/atoms/public-route/public-route'
// components
import Layout from 'components/organisms/layout/layout'
// routes
import { currentHome } from 'components/organisms/nav-menu/nav-menu-routes'
// layout props
import LayoutProps from 'constants/layoutProps'
// services
import AuthService from 'modules/Auth/services/auth'
import listRoutes from './listRoutes'
import { useIdleTimer } from 'hooks/useCleanStorage'

const HomePage = lazy(() => import('../modules/Home/components/pages/home-page'))
const EmailValidationPage = lazy(() =>
	import('../modules/Auth/components/pages/email-validation-page')
)
const LoginPage = lazy(() => import('../modules/Auth/components/pages/login-page'))
const IntroSlidesPage = lazy(() => import('../components/pages/intro-slides-page'))
const MyRoutine = lazy(() => import('../components/pages/for-today-page'))
const MyRoutinePersonalPage = lazy(() =>
	import('../modules/Routine/components/pages/my-routine-personal-page')
)
const HealthAssistantPage = lazy(() => import('../components/pages/health-assistant-page'))
const OnboardingPage = lazy(() => import('../components/pages/onboarding-page'))
const ProfilePage = lazy(() => import('../modules/Profile/components/pages/profile-page'))
const ToolPage = lazy(() => import('../components/pages/tools-page'))
const CalculatorIMC = lazy(() => import('../modules/Profile/components/pages/calculator-imc'))
const CarerAddPatientPage = lazy(() => import('../components/pages/carer-add-patient-page'))
const PatientAddPage = lazy(() => import('../components/pages/patient-add-page'))
const TermsAndConditionsPage = lazy(() =>
	import('../modules/Auth/components/pages/terms-and-condition-page')
)
const DailyInfoPage = lazy(() => import('../components/pages/dayli-info-page'))
const ParQPublicPage = lazy(() => import('../modules/ParQ/components/pages/par-q-public-page'))
const YourConditionPage = lazy(() =>
	import('../modules/Profile/components/pages/your-condition-page')
)
const YourHabitsPage = lazy(() => import('../modules/Habits/components/pages/your-habits-page'))
const HabitsPage = lazy(() => import('../modules/Habits/components/pages/habits-page'))
const CareNetworkPage = lazy(() =>
	import('../modules/CareNetwork/components/pages/care-network-page')
)
const CareNetworkPatientPage = lazy(() =>
	import('../modules/CareNetwork/components/pages/care-network-patient-page')
)
const ServicesPage = lazy(() => import('../modules/Services/components/pages/services-page'))
const ParQPage = lazy(() => import('../modules/ParQ/components/pages/par-q-page'))
const ParQCareCenterWelcomePage = lazy(() =>
	import('../modules/ParQ/components/pages/ParQCareCenterWelcomePage')
)
const AppointmentPage = lazy(() => import('../modules/Appoiment/components/pages/appointment-page'))
const MyAppointmentPage = lazy(() => import('../components/pages/my-appointment-page'))
const ProgramPage = lazy(() => import('../modules/Programs/components/pages/program-page'))
const ProgramInternalPage = lazy(() =>
	import('../modules/Programs/components/pages/program-internal-page')
)

const ProgramActivityInternal = lazy(() =>
	import('../modules/Programs/components/pages/program-activity-internal-page')
)

const ProgramSearchPage = lazy(() =>
	import('../modules/Programs/components/pages/program-search-page')
)

const CompleteDataUserPage = lazy(() =>
	import('../modules/Profile/components/pages/complete-data-user')
)

const CompanyBeneftisPage = lazy(() =>
	import('../modules/CompanyBenefits/components/pages/company-benefits-page')
)

const ProgramQualify = lazy(() => import('../components/pages/program-qualify'))
const LifeSurveyPage = lazy(() => import('../components/pages/life-survey-page'))
const SadriPage = lazy(() => import('../modules/Sadri/Welcome.page'))
const CareShop = lazy(() => import('../modules/CareShop/CareShop.Page'))
const SadriAnalysisPage = lazy(() => import('../modules/Sadri/Analysis.page'))
const PacPage = lazy(() => import('../components/pages/pac-page'))
const MyServicesPage = lazy(() => import('../components/pages/my-services-page'))

const NotFoundRedirect = ({ isLogged }) => {
	if (isLogged) {
		return <Redirect to={currentHome} />
	}
	return <Redirect to='/' />
}

const Routes = () => {
	useIdleTimer()
	// redux
	const {
		session: { isLogin }
	} = useSelector(state => state)
	// location
	const { pathname: route, search } = useLocation()

	const routeWithSearch = `${route}${search}`

	// data
	const isLogged = AuthService.isLoggedIn(true) && isLogin

	// init CIS
	routeWithSearch.includes('CIS') && localStorage.setItem('USER_CIS', 'true')

	// layout props
	const routeLayout = () => {
		const currentRoute = Object.keys(LayoutProps).filter(e => {
			return e.includes(routeWithSearch) || routeWithSearch.includes(e)
		})

		return currentRoute && currentRoute.length > 0
			? LayoutProps[routeWithSearch] || LayoutProps[currentRoute.pop()]
			: LayoutProps.default
	}

	return (
		<Layout {...routeLayout()}>
			<Suspense fallback={<Loading />}>
				<Switch>
					<Route exact path={listRoutes.slides} component={IntroSlidesPage} />
					<Route exact path={listRoutes.parQPublic} component={ParQPublicPage} />
					<PublicRoute exact path={listRoutes.emailValidation} component={EmailValidationPage} />
					<PublicRoute exact path={listRoutes.login} component={LoginPage} />
					<PrivateRoute
						exact
						path={listRoutes.yourServices}
						component={HomePage}
						validateRegister
					/>
					<PrivateRoute exact path={listRoutes.initInformation} component={OnboardingPage} />
					<PrivateRoute exact path={listRoutes.dailyInformation} component={DailyInfoPage} />
					<PrivateRoute exact path={listRoutes.profile} component={ProfilePage} />
					<PrivateRoute
						exact
						path={listRoutes.termsAndConditions}
						component={TermsAndConditionsPage}
					/>
					<PrivateRoute exact path={listRoutes.addPatient} component={PatientAddPage} />
					<PrivateRoute exact path={listRoutes.validPatient} component={CarerAddPatientPage} />
					<PrivateRoute exact path={listRoutes.IMC} component={CalculatorIMC} />
					<PrivateRoute exact path={`${listRoutes.tools}/:routeWithSearch`} component={ToolPage} />
					<PrivateRoute exact path={listRoutes.myRoutine} component={MyRoutine} />
					<PrivateRoute
						exact
						path={listRoutes.myPersonalRoutine}
						component={MyRoutinePersonalPage}
					/>
					<PrivateRoute exact path={listRoutes.yourCondition} component={YourConditionPage} />
					<PrivateRoute exact path={listRoutes.sadri} component={SadriPage} />
					<PrivateRoute exact path={listRoutes.careShop} component={CareShop} />
					<PrivateRoute
						exact
						path={`${listRoutes.sadri}${listRoutes.sadriAnalysis}`}
						component={SadriAnalysisPage}
					/>
					<PrivateRoute exact path={listRoutes.yourHabits} component={YourHabitsPage} />
					<PrivateRoute exact path={`${listRoutes.habits}/:dimension`} component={HabitsPage} />
					<PrivateRoute exact path={listRoutes.healthAsistan} component={HealthAssistantPage} />
					<PrivateRoute exact path={listRoutes.careNetwork} component={CareNetworkPage} />
					<PrivateRoute
						exact
						path={`${listRoutes.careNetwork}/:userId`}
						component={CareNetworkPatientPage}
					/>
					<PrivateRoute exact path={listRoutes.completeDataUser} component={CompleteDataUserPage} />
					<PrivateRoute
						exact
						path={`${listRoutes.services}/:id/:userId/:serviceId`}
						component={ServicesPage}
					/>
					<Route
						path={listRoutes.parQWelcome}
						element={<PrivateRoute component={ParQCareCenterWelcomePage} />}
					/>
					<Route path={listRoutes.parQ} element={<PrivateRoute component={ParQPage} />} />
					<Route
						path={listRoutes.appointment}
						element={<PrivateRoute component={AppointmentPage} />}
					/>
					<Route
						path={listRoutes.myAppointment}
						element={<PrivateRoute component={MyAppointmentPage} />}
					/>
					<PrivateRoute exact path={listRoutes.parQWelcome} component={ParQCareCenterWelcomePage} />
					<PrivateRoute exact path={listRoutes.parQ} component={ParQPage} />
					<PrivateRoute exact path={listRoutes.appointment} component={AppointmentPage} />
					<PrivateRoute exact path={listRoutes.myAppointment} component={MyAppointmentPage} />

					{/* programs */}
					<PrivateRoute exact path={listRoutes.program} component={ProgramPage} />
					<PrivateRoute
						exact
						path={`${listRoutes.programInternal}/:id/:programUserId`}
						component={ProgramInternalPage}
					/>

					<PrivateRoute
						exact
						path={`${listRoutes.programInternal}/:id/:programUserId/actividades`}
						component={ProgramActivityInternal}
					/>
					<PrivateRoute exact path={listRoutes.programSearch} component={ProgramSearchPage} />
					<PrivateRoute
						exact
						path={`${listRoutes.programSuscribeProgram}/:id`}
						component={ProgramInternalPage}
					/>
					<PrivateRoute exact path={listRoutes.programQualify} component={ProgramQualify} />
					<PrivateRoute
						exact
						path={`${listRoutes.companyBenefits}/:benefit`}
						component={CompanyBeneftisPage}
					/>
					<PrivateRoute exact path={listRoutes.lifestyleQuestionnaire} component={LifeSurveyPage} />
					<PrivateRoute exact path={listRoutes.pac} component={PacPage} />
					<PrivateRoute exact path={listRoutes.myServices} component={MyServicesPage} />
					<Route path='**' render={() => <NotFoundRedirect isLogged={isLogged} />} />
				</Switch>
			</Suspense>
		</Layout>
	)
}

export default Routes
